import type {
  GenerationImpactResult,
  GenerationImpactScenarioInput,
  GenerationUnit,
} from '../../types/generationImpact';
import {
  GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE,
  isGridImportOnlyScenario,
  validateGenerationSizingPhysicalInputs,
} from './generationSizingValidation';

export const DEFAULT_BLOCK_SIZING_INCREMENT_MW = 10;

export type UniformBlockSizingResult =
  | {
      status: 'available';
      blockCount: number;
      onlineBlocksAfterContingency: number;
      configuredGrossMWPerBlock: number;
      netDeliveryFactor: number;
      requiredLoadGrossMWPerBlock: number;
      requiredReserveGrossMWPerBlock: number;
      suggestedLoadGrossMWPerBlock: number;
      suggestedReserveGrossMWPerBlock: number;
      configuredMeetsLoad: boolean;
      configuredMeetsReserve: boolean;
      incrementMW: number;
    }
  | {
      status: 'unavailable';
      reason: string;
    };

const RELATIVE_TOLERANCE = 1e-8;

function nearlyEqual(a: number, b: number): boolean {
  const scale = Math.max(1, Math.abs(a), Math.abs(b));
  return Math.abs(a - b) <= RELATIVE_TOLERANCE * scale;
}

function auxiliaryFraction(unit: GenerationUnit): number {
  return unit.isoGrossMWPerUnit > 0
    ? unit.auxiliaryLoadMWPerUnit / unit.isoGrossMWPerUnit
    : Number.NaN;
}

function hasUniformBlockBasis(units: readonly GenerationUnit[]): boolean {
  const first = units[0];
  if (!first) return false;
  const firstAuxiliaryFraction = auxiliaryFraction(first);
  return units.every((unit) => (
    unit.count === 1
    && unit.technology === first.technology
    && nearlyEqual(unit.isoGrossMWPerUnit, first.isoGrossMWPerUnit)
    && nearlyEqual(unit.ambientFactor, first.ambientFactor)
    && nearlyEqual(unit.degradationFactor, first.degradationFactor)
    && nearlyEqual(auxiliaryFraction(unit), firstAuxiliaryFraction)
    && nearlyEqual(unit.heatRateBtuPerKWhHhv, first.heatRateBtuPerKWhHhv)
  ));
}

/** Shared with the granularity sweep so both round block sizes identically. */
export function roundUpToIncrement(value: number, incrementMW: number): number {
  return Math.ceil((value - Number.EPSILON) / incrementMW) * incrementMW;
}

const roundUp = roundUpToIncrement;

/**
 * Invert the deterministic solver for the common "N identical blocks" duty.
 *
 * The calculation is intentionally unavailable for mixed fleets or grouped
 * contingencies. For a uniform fleet, net output remains linear with ISO gross
 * MW when ambient/degradation factors, auxiliary fraction, heat rate and CCS
 * basis are held constant, so the exact solver result supplies the delivery
 * coefficient without duplicating its thermodynamic or CCS equations.
 */
export function sizeUniformGenerationBlocks(
  input: GenerationImpactScenarioInput,
  result: GenerationImpactResult,
  incrementMW = DEFAULT_BLOCK_SIZING_INCREMENT_MW,
): UniformBlockSizingResult {
  if (isGridImportOnlyScenario(input)) {
    return { status: 'unavailable', reason: GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE };
  }
  if (!Number.isFinite(incrementMW) || incrementMW <= 0) {
    return { status: 'unavailable', reason: 'The block-sizing increment must be greater than zero.' };
  }
  if (input.units.length < 2) {
    return { status: 'unavailable', reason: 'At least two independent blocks are required for an N-1 sizing envelope.' };
  }
  const physicalInputErrors = validateGenerationSizingPhysicalInputs(input);
  if (physicalInputErrors.length > 0) {
    return { status: 'unavailable', reason: physicalInputErrors.join(' ') };
  }
  if (!hasUniformBlockBasis(input.units)) {
    return { status: 'unavailable', reason: 'Entered-versus-required sizing is available only for identical one-unit blocks.' };
  }
  if (new Set(input.units.map((unit) => unit.contingencyGroupId)).size !== input.units.length) {
    return { status: 'unavailable', reason: 'Each block must have an independent contingency group for uniform N-1 sizing.' };
  }
  if (result.gaps.length > 0) {
    return { status: 'unavailable', reason: 'Resolve the scenario input gaps before calculating a block-sizing envelope.' };
  }
  if (!Number.isFinite(result.capacity.criticalLoadMW) || result.capacity.criticalLoadMW <= 0) {
    return { status: 'unavailable', reason: 'Enter a positive critical load before calculating a block-sizing envelope.' };
  }

  const configuredGrossMWPerBlock = input.units[0]!.isoGrossMWPerUnit;
  const netMWPerBlock = result.unitResults[0]?.netMWPerUnit;
  const onlineBlocksAfterContingency = input.units.length - 1;
  if (
    !Number.isFinite(configuredGrossMWPerBlock)
    || configuredGrossMWPerBlock <= 0
    || netMWPerBlock == null
    || !Number.isFinite(netMWPerBlock)
    || netMWPerBlock <= 0
  ) {
    return { status: 'unavailable', reason: 'A positive configured gross and net output are required for block sizing.' };
  }

  const netDeliveryFactor = netMWPerBlock / configuredGrossMWPerBlock;
  const survivingFleetFactor = onlineBlocksAfterContingency * netDeliveryFactor;
  const requiredLoadGrossMWPerBlock = result.capacity.criticalLoadMW / survivingFleetFactor;
  const requiredReserveGrossMWPerBlock = result.capacity.reserveRequiredMW / survivingFleetFactor;

  return {
    status: 'available',
    blockCount: input.units.length,
    onlineBlocksAfterContingency,
    configuredGrossMWPerBlock,
    netDeliveryFactor,
    requiredLoadGrossMWPerBlock,
    requiredReserveGrossMWPerBlock,
    suggestedLoadGrossMWPerBlock: roundUp(requiredLoadGrossMWPerBlock, incrementMW),
    suggestedReserveGrossMWPerBlock: roundUp(requiredReserveGrossMWPerBlock, incrementMW),
    configuredMeetsLoad: result.capacity.nMinusOneVerdict === 'pass',
    configuredMeetsReserve: result.capacity.nMinusOneReserveVerdict === 'pass',
    incrementMW,
  };
}
